import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardHeaderBtn,
  CardBody,
} from "./../../components/card/card.jsx";
import Swal from "sweetalert2";
import { Circles } from "react-loader-spinner";
import "moment/locale/es";

const ACCESS_TOKEN =
  "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";

const API_BASE_URL = "https://api.checkmark.mx/";

const METODO_CONTACTO_OPTIONS = [
  "Redes sociales",
  "Punto de venta",
  "Carabana",
  "Volanteo",
  "Recomendación",
  "Publicidad en la calle",
];

const ORIGEN_CLIENTE_OPTIONS = ["Cartera", "Piso", "Digital", "Calle"];

const INITIAL_CLIENTE = {
  nombre: "",
  telefono: "",
  correo: "",
};

const INITIAL_SELECTED_VEHICLE = {
  brand: "",
  model: "",
  ano: "",
  version: "",
  agency: "",
};

const INITIAL_FINANCIAMIENTO = {
  precioVehiculo: "",
  enganche: "",
  tasa: "",
  mensualidad: "",
  plazo: "",
  financiera: "",
  metodoContacto: "",
  origenCliente: "",
  aseguradora: "",
  montoSeguro: "",
  tipoCompraSeguro: "",
  montoComisionApertura: "",
  tipoComision: "",
  incluyePlacas: "No",
  montoPlacas: "",
  respuestaPruebaManejo: "",
  userId: "",
  asesor: "",
};

const normalizarTexto = (texto = "") =>
  String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\-_\/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const limpiarModeloVersion = (texto = "") =>
  normalizarTexto(texto)
    .replace(/\b(2024|2025|2026|2027|2028|2029|24|25|26|27|28|29)\b/g, "")
    .replace(/\s+/g, " ")
    .trim();

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;

  const cleanValue = String(value)
    .replace(/,/g, "")
    .replace(/\$/g, "")
    .replace(/%/g, "")
    .trim();

  const numberValue = Number(cleanValue.replace(/[^0-9.-]/g, ""));

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const cleanNumberInput = (value) => {
  const cleanValue = String(value || "")
    .replace(/,/g, "")
    .replace(/\$/g, "")
    .replace(/%/g, "")
    .replace(/[^0-9.]/g, "");

  const parts = cleanValue.split(".");

  if (parts.length <= 1) return cleanValue;

  return `${parts[0]}.${parts.slice(1).join("")}`;
};

const formatThousands = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const cleanValue = cleanNumberInput(value);
  if (!cleanValue) return "";

  const [integerPart, decimalPart] = cleanValue.split(".");
  const formattedInteger = Number(integerPart || 0).toLocaleString("es-MX");

  if (cleanValue.endsWith(".")) return `${formattedInteger}.`;
  if (decimalPart !== undefined) return `${formattedInteger}.${decimalPart}`;

  return formattedInteger;
};

const formatCurrency = (value) => {
  const numberValue = toNumber(value);

  return numberValue.toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
  });
};

const cleanName = (name = "") =>
  String(name || "")
    .trim()
    .replace(/^(ma|m\.a\.|ma\.)\s+/i, "")
    .trim();

const formatName = (name = "") =>
  String(name || "")
    .toLowerCase()
    .replace(/(^\p{L}|\s\p{L})/gu, (char) => char.toUpperCase());

const quitarAcentos = (texto = "") => {
  const mapaAcentos = {
    á: "a",
    é: "e",
    í: "i",
    ó: "o",
    ú: "u",
    ü: "u",
    ñ: "n",
    Á: "A",
    É: "E",
    Í: "I",
    Ó: "O",
    Ú: "U",
    Ü: "U",
    Ñ: "N",
  };

  return String(texto || "").replace(
    /([áéíóúüñÁÉÍÓÚÜÑ])/g,
    (match) => mapaAcentos[match] || match,
  );
};

const limpiarJsonOpenAI = (texto = "") =>
  String(texto || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

const parseOpenAIJson = (texto = "") => {
  const limpio = limpiarJsonOpenAI(texto);

  try {
    const parsed = JSON.parse(limpio);
    return parsed?.datos || parsed || {};
  } catch (error) {
    console.warn("No se pudo parsear JSON directo de OpenAI:", error);
  }

  return {};
};

const getValue = (obj, keys = []) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null && obj?.[key] !== "") {
      return obj[key];
    }
  }

  return "";
};

const estaDisponible = (item) =>
  item?.available === true ||
  item?.available === "true" ||
  item?.available === 1 ||
  item?.available === "1" ||
  item?.disponible === true ||
  item?.disponible === "true" ||
  item?.active === true ||
  item?.active === "true" ||
  item?.activo === 1 ||
  item?.activo === "1";

const coincideAgenciaItem = (item, agIdActivo = "") => {
  const itemAgId = String(item?.ag_id || "")
    .trim()
    .toLowerCase();
  const agId = String(agIdActivo || "")
    .trim()
    .toLowerCase();

  if (!itemAgId || itemAgId === "all") return true;
  if (!agId) return false;

  return itemAgId === agId;
};

const coincideMarcaItem = (item, marcaActiva = "") => {
  const itemBrand = normalizarTexto(item?.brand || item?.marca || "");
  const marca = normalizarTexto(marcaActiva || "");

  if (!itemBrand || itemBrand === "all") return true;
  if (!marca) return false;

  return itemBrand === marca;
};

const getAccessoryPrice = (item) =>
  toNumber(
    item?.price_promo ??
      item?.precio_promo ??
      item?.price ??
      item?.precio ??
      item?.price_base ??
      item?.precio_base ??
      0,
  );

const normalizeAccessoryForSave = (item) => {
  const price = getAccessoryPrice(item);

  return {
    id: item?.id || item?.ag_id || item?.title || "",
    title: item?.title || item?.titulo || "",
    description: item?.description || item?.descripcion || "",
    price,
    price_promo: toNumber(item?.price_promo ?? item?.precio_promo ?? price),
    price_base: toNumber(item?.price_base ?? item?.precio_base ?? price),
    type: item?.type || item?.tipo || "accesorios",
    image: item?.image || item?.img || item?.url_img || "",
    ag_id: item?.ag_id || "",
    brand: item?.brand || item?.marca || "",
  };
};

function MoneyInput({ id, value, onChange, disabled, placeholder = "0" }) {
  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      className="form-control"
      placeholder={placeholder}
      value={formatThousands(value)}
      onChange={(e) => onChange(cleanNumberInput(e.target.value))}
      disabled={disabled}
    />
  );
}

function CreateRequest() {
  const modules = JSON.parse(localStorage.getItem("modulos")) || [];
  const location = useLocation();
  const inventarioRequestIdRef = useRef(0);
  const inventarioCacheRef = useRef({});
  const fileInputRef = useRef(null);

  const tabs = [
    { name: "Crear", to: "/deals/request", module: "deals-request" },
    { name: "Solicitudes", to: "/deals/update", module: "deals-update" },
    { name: "Resumen", to: "/deals/requests", module: "deals-requests" },
    { name: "Ranking", to: "/deals/ranking", module: "deals-ranking" },
  ];

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [agencias, setAgencias] = useState([]);
  const [selectedAgencia, setSelectedAgencia] = useState("");
  const [asesores, setAsesores] = useState([]);
  const [cargandoAsesores, setCargandoAsesores] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [tableData, setTableData] = useState({ food: [] });
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventoryError, setInventoryError] = useState("");
  const [selectedAccessoryIds, setSelectedAccessoryIds] = useState([]);

  const [cliente, setCliente] = useState(INITIAL_CLIENTE);
  const [selectedVehicle, setSelectedVehicle] = useState(
    INITIAL_SELECTED_VEHICLE,
  );
  const [financiamiento, setFinanciamiento] = useState(INITIAL_FINANCIAMIENTO);

  const [condicionVehiculo, setCondicionVehiculo] = useState("nuevos");

  const tipoAutoNormalizado = useMemo(() => {
    const value = normalizarTexto(condicionVehiculo);
    return value.includes("semi") ? "seminuevos" : "nuevos";
  }, [condicionVehiculo]);

  const selectedAgency = useMemo(() => {
    return agencias.find(
      (agency) => String(agency.ag_id) === String(selectedAgencia),
    );
  }, [agencias, selectedAgencia]);

  const selectedAgencyName =
    selectedAgency?.nombre || selectedVehicle.agency || "";

  const getAsesorId = (asesor) => {
    return (
      asesor?.id ||
      asesor?.user_id ||
      asesor?.usuario_id ||
      asesor?.id_usuario ||
      asesor?.uid ||
      ""
    );
  };

  const getAsesorNombre = (asesor) => {
    return (
      asesor?.nombre ||
      asesor?.name ||
      asesor?.asesor ||
      asesor?.usuario ||
      asesor?.nombre_usuario ||
      asesor?.correo ||
      ""
    );
  };

  const getAsesorNombreDisplay = (asesor) =>
    formatName(cleanName(getAsesorNombre(asesor)));

  const asesoresOrdenados = [...asesores].sort((a, b) => {
    const an = cleanName(getAsesorNombre(a));
    const bn = cleanName(getAsesorNombre(b));
    return an.localeCompare(bn, "es", { sensitivity: "base" });
  });

  const selectedAsesor = useMemo(() => {
    return asesores.find(
      (asesor) => String(getAsesorId(asesor)) === String(financiamiento.userId),
    );
  }, [asesores, financiamiento.userId]);

  const asesorNombre = selectedAsesor
    ? getAsesorNombreDisplay(selectedAsesor)
    : financiamiento.asesor || "";

  const precioVehiculoBase = useMemo(
    () => toNumber(financiamiento.precioVehiculo),
    [financiamiento.precioVehiculo],
  );

  const filteredAccessoriesAndWarranties = useMemo(() => {
    const tipoAuto = normalizarTexto(tipoAutoNormalizado || "");
    const modeloAuto = normalizarTexto(selectedVehicle.model || "");
    const versionAuto = normalizarTexto(selectedVehicle.version || "");
    const marcaActual = normalizarTexto(selectedVehicle.brand || "");
    const agIdActual = selectedAgencia || "";

    const soloParaNuevos = [
      "Garantía Extendida",
      "Cobertura de Llaves",
      "Canastilla de techo",
      "Rejilla Separadora",
      "Tapete uso rudo area de carga",
      "Organizador de Cajuela",
      "Seguro LLantas",
    ].map(normalizarTexto);

    const soloParaSeminuevos = ["Kit de seguridad", "Localizador Gocar"].map(
      normalizarTexto,
    );

    const esNuevo = tipoAuto === "nuevos" || tipoAuto === "nuevo";
    const esSeminuevo = tipoAuto === "seminuevos" || tipoAuto === "seminuevo";

    if (!Array.isArray(tableData?.food)) return [];

    return tableData.food
      .filter((item) => {
        if (!item || item.hide) return false;
        if (!estaDisponible(item)) return false;
        if (!coincideMarcaItem(item, marcaActual)) return false;
        if (!coincideAgenciaItem(item, agIdActual)) return false;

        const itemType = normalizarTexto(item.type || "");
        const esGarantia = itemType === "garantias";
        const esAccesorio = itemType === "accesorios";
        const tituloNorm = normalizarTexto(item.title || "");

        if (!esGarantia && !esAccesorio) return false;

        const esProteccion5000 =
          esGarantia &&
          tituloNorm.includes("proteccion de llanta rin") &&
          tituloNorm.includes("5,000");

        const esProteccion12000 =
          esGarantia &&
          tituloNorm.includes("proteccion de llanta rin") &&
          tituloNorm.includes("12,000");

        if (esSeminuevo && (esProteccion5000 || esProteccion12000)) {
          return false;
        }

        const esGarantiaMecanica =
          tituloNorm.includes("garantia mecanica") ||
          tituloNorm.includes("mecanica amplia");

        if (esNuevo && esGarantiaMecanica) return false;

        const esSoloSeminuevos = soloParaSeminuevos.some((palabra) =>
          tituloNorm.includes(palabra),
        );

        if (esNuevo && esSoloSeminuevos) return false;

        const esSoloParaNuevos = soloParaNuevos.some((palabra) =>
          tituloNorm.includes(palabra),
        );

        if (esSeminuevo && esSoloParaNuevos) return false;

        if (String(item.title || "").includes(" - ")) {
          const [, rawModelos = ""] = String(item.title || "").split(" - ");

          const modelosLista = rawModelos
            .split(",")
            .map((m) => limpiarModeloVersion(m))
            .filter(Boolean);

          const incluyeTodos = modelosLista.includes("todos");
          const modeloLimpio = limpiarModeloVersion(modeloAuto);
          const versionLimpia = limpiarModeloVersion(versionAuto);
          const modeloVersion = limpiarModeloVersion(
            `${modeloLimpio} ${versionLimpia}`,
          );

          const coincideModelo =
            !!modeloLimpio && modelosLista.includes(modeloLimpio);
          const coincideModeloVersion =
            !!modeloVersion && modelosLista.includes(modeloVersion);

          const coincide = esGarantia
            ? incluyeTodos || coincideModelo || coincideModeloVersion
            : incluyeTodos || coincideModelo;

          if (!coincide) return false;
        }

        return true;
      })
      .map((item, index) => {
        let finalTitle = item.title || "";

        if (finalTitle.includes(" - ")) {
          finalTitle = finalTitle.split(" - ")[0];
        }

        return {
          ...item,
          title: finalTitle,
          __manual_id: String(item.id || `${finalTitle}-${item.type}-${index}`),
        };
      });
  }, [
    tableData?.food,
    tipoAutoNormalizado,
    selectedVehicle.model,
    selectedVehicle.version,
    selectedVehicle.brand,
    selectedAgencia,
  ]);

  const selectedAccessories = useMemo(() => {
    const selectedSet = new Set(selectedAccessoryIds.map(String));

    return filteredAccessoriesAndWarranties
      .filter((item) => selectedSet.has(String(item.__manual_id)))
      .map(normalizeAccessoryForSave);
  }, [selectedAccessoryIds, filteredAccessoriesAndWarranties]);

  const totalAccesorios = useMemo(() => {
    return selectedAccessories.reduce(
      (total, accessory) => total + toNumber(accessory.price),
      0,
    );
  }, [selectedAccessories]);

  const precioTotalFinal = useMemo(() => {
    return Math.round(precioVehiculoBase + totalAccesorios);
  }, [precioVehiculoBase, totalAccesorios]);

  const planCliente = useMemo(
    () => ({
      title: "Plan cliente",
      accessories: selectedAccessories,
      totalPrice: totalAccesorios,
      totalPrice_promo: totalAccesorios,
      totalPrice_base: totalAccesorios,
      isSelected: true,
    }),
    [selectedAccessories, totalAccesorios],
  );

  const selectedCarManual = useMemo(
    () => ({
      title: selectedVehicle.brand,
      marca: selectedVehicle.brand,
      description: selectedVehicle.model,
      modelo: selectedVehicle.model,
      year: selectedVehicle.ano,
      ano: selectedVehicle.ano,
      version: selectedVehicle.version,
      price: precioVehiculoBase,
      precio_vehiculo: precioVehiculoBase,
      precio_total: precioTotalFinal,
      type: tipoAutoNormalizado,
      ag_id: selectedAgencia,
      agencia: selectedAgencyName,
      availableOptions: {
        size: selectedVehicle.version
          ? [
              {
                text: selectedVehicle.version,
                price: String(precioVehiculoBase),
              },
            ]
          : [],
      },
    }),
    [
      selectedVehicle,
      precioVehiculoBase,
      precioTotalFinal,
      tipoAutoNormalizado,
      selectedAgencia,
      selectedAgencyName,
    ],
  );

  const orderCartManual = useMemo(() => {
    const vehicleItem = {
      title: selectedVehicle.brand,
      description: selectedVehicle.model,
      year: selectedVehicle.ano,
      version: selectedVehicle.version,
      price: precioVehiculoBase,
      quantity: 1,
      type: tipoAutoNormalizado,
      ag_id: selectedAgencia,
      availableOptions: selectedCarManual.availableOptions,
    };

    const accessoryItems = selectedAccessories.map((item) => ({
      ...item,
      quantity: 1,
    }));

    return [vehicleItem, ...accessoryItems];
  }, [
    selectedVehicle,
    precioVehiculoBase,
    tipoAutoNormalizado,
    selectedAgencia,
    selectedCarManual.availableOptions,
    selectedAccessories,
  ]);

  useEffect(() => {
    const validIds = new Set(
      filteredAccessoriesAndWarranties.map((item) => String(item.__manual_id)),
    );

    setSelectedAccessoryIds((prev) =>
      prev.filter((itemId) => validIds.has(String(itemId))),
    );
  }, [filteredAccessoriesAndWarranties]);

  const cargarAgencias = async () => {
    let agenciasStorage = [];

    try {
      agenciasStorage = JSON.parse(localStorage.getItem("agencias")) || [];
    } catch (error) {
      console.error("No se pudieron leer agencias del localStorage:", error);
      agenciasStorage = [];
    }

    if (!agenciasStorage.length || !ACCESS_TOKEN) {
      console.error("No hay agencias o access token disponibles.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}financiamiento/usuario/agencias`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            access_token: ACCESS_TOKEN,
            agencias: agenciasStorage,
          }),
        },
      );

      const data = await response.json();

      if (response.ok && data.data) {
        setAgencias(data.data);
      } else {
        console.error("Error al cargar las agencias:", data.message);
      }
    } catch (error) {
      console.error("Error en la solicitud de agencias:", error);
    }
  };

  const cargarAsesoresPorAgencia = async (agId) => {
    if (!agId) {
      setAsesores([]);
      return;
    }

    try {
      setCargandoAsesores(true);

      const response = await fetch(`${API_BASE_URL}obtener/asesores/agencia`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: ACCESS_TOKEN,
          ag_id: agId,
        }),
      });

      const data = await response.json();

      const listaAsesores =
        data?.statusCode === 200 && Array.isArray(data?.data?.asesores)
          ? data.data.asesores
          : [];

      setAsesores(listaAsesores);
    } catch (error) {
      console.error("Error al cargar asesores:", error);
      setAsesores([]);
    } finally {
      setCargandoAsesores(false);
    }
  };

  const cargarInventarioAccesorios = useCallback(async () => {
    const agId = String(selectedAgencia || "").trim();
    const marca = String(selectedVehicle.brand || "").trim();

    if (!agId || !marca) {
      setTableData({ food: [] });
      setInventoryError("");
      return;
    }

    const cacheKey = `${agId}__${normalizarTexto(marca)}`;

    if (inventarioCacheRef.current?.[cacheKey]) {
      setTableData(inventarioCacheRef.current[cacheKey]);
    }

    const requestId = ++inventarioRequestIdRef.current;

    try {
      setLoadingInventory(true);
      setInventoryError("");

      const response = await fetch(`${API_BASE_URL}financiamiento/vehiculos`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: ACCESS_TOKEN,
          ag_id: agId,
          marca,
        }),
      });

      const data = await response.json();

      if (requestId !== inventarioRequestIdRef.current) return;

      const foodOriginal = Array.isArray(data?.food) ? data.food : [];

      const foodFiltrado = foodOriginal.filter((item) => {
        const itemType = normalizarTexto(item?.type || "");

        if (itemType === "accesorios" || itemType === "garantias") {
          return (
            !item?.hide &&
            estaDisponible(item) &&
            coincideMarcaItem(item, marca) &&
            coincideAgenciaItem(item, agId)
          );
        }

        return false;
      });

      const dataLimpia = {
        ...data,
        food: foodFiltrado,
      };

      inventarioCacheRef.current[cacheKey] = dataLimpia;
      setTableData(dataLimpia);
    } catch (error) {
      console.error("Error al cargar accesorios y garantías:", error);
      setInventoryError(
        "No se pudo cargar el inventario de accesorios y garantías.",
      );
    } finally {
      setLoadingInventory(false);
    }
  }, [selectedAgencia, selectedVehicle.brand]);

  useEffect(() => {
    cargarAgencias();
  }, []);

  useEffect(() => {
    cargarInventarioAccesorios();
  }, [cargarInventarioAccesorios]);

  const handleAgenciaChange = async (e) => {
    const agId = e.target.value;

    const agenciaSeleccionada = agencias.find(
      (agencia) => String(agencia.ag_id) === String(agId),
    );

    setSelectedAgencia(agId);
    setAsesores([]);
    setSelectedAccessoryIds([]);

    setSelectedVehicle((prevState) => ({
      ...prevState,
      agency: agenciaSeleccionada?.nombre || "",
    }));

    setFinanciamiento((prevState) => ({
      ...prevState,
      userId: "",
      asesor: "",
    }));

    await cargarAsesoresPorAgencia(agId);
  };

  const handleAsesorChange = (e) => {
    const userId = e.target.value;

    const asesorSeleccionado = asesores.find(
      (asesor) => String(getAsesorId(asesor)) === String(userId),
    );

    setFinanciamiento((prevState) => ({
      ...prevState,
      userId,
      asesor: asesorSeleccionado
        ? getAsesorNombreDisplay(asesorSeleccionado)
        : "",
    }));
  };

  const handleClienteChange = (field, value) => {
    setCliente((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleVehicleChange = (field, value) => {
    setSelectedVehicle((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    if (["brand", "model", "version"].includes(field)) {
      setSelectedAccessoryIds([]);
    }
  };

  const handleFinanciamientoChange = (field, value) => {
    setFinanciamiento((prevState) => {
      const nextState = {
        ...prevState,
        [field]: value,
      };

      if (field === "incluyePlacas" && value !== "Si") {
        nextState.montoPlacas = "";
      }

      return nextState;
    });
  };

  const toggleAccessory = (item) => {
    const id = String(item.__manual_id);

    setSelectedAccessoryIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((itemId) => itemId !== id);
      }

      return [...prev, id];
    });
  };

  const obtenerTokenOpenIA = async () => {
    const formData = new FormData();
    formData.append("access_token", ACCESS_TOKEN);

    const res = await fetch(`${API_BASE_URL}obtener/token/openia`, {
      method: "POST",
      body: formData,
    });

    const data = await res.json();

    if (!res.ok || data.statusCode !== 200) {
      throw new Error(data.message || "No se pudo obtener el token de OpenIA");
    }

    const tokenOpenIA = data?.data?.token_openia?.access_token;

    if (!tokenOpenIA) {
      throw new Error("El servicio no devolvió access_token de OpenIA");
    }

    return tokenOpenIA;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);

    const formData = new FormData();
    formData.append("apikey", "DPD8VZYKE82MX");
    formData.append("file", file);
    formData.append("language", "spa");

    try {
      setLoading(true);

      const res = await fetch("https://apipro1.ocr.space/parse/image", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      const text = data?.ParsedResults?.[0]?.ParsedText || "";

      if (!text) {
        Swal.fire({
          icon: "warning",
          title: "PDF cargado",
          text: "El PDF se cargó correctamente, pero no se pudo leer automáticamente. Llena los datos manualmente.",
        });
        return;
      }

      const textFormateado = quitarAcentos(text);
      const textoPlano = textFormateado.replace(/(\r\n|\n|\r)/g, " ").trim();
      const tokenOpenIA = await obtenerTokenOpenIA();

      const responseOpenAI = await fetch(
        "https://api.openai.com/v1/chat/completions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenOpenIA}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            model: "gpt-4o-mini",
            max_tokens: 1000,
            messages: [
              {
                role: "user",
                content:
                  "Generame solamente un JSON válido, sin markdown, que contenga un objeto llamado datos, con estos datos si existen: Marca, Año, Modelo, Version, Cliente, Telefono, Correo, PrecioVehiculo, PrecioTotal, PlazoMeses, Enganche, Mensualidad, Banco, Financiera, TipoDeVehiculo, PlanDeFinanciamiento, TasaDeInteres, Agencia, Distribuidor, MetodoContacto, OrigenCliente, Fuente, FuenteOrigen, Aseguradora, MontoSeguro, TipoCompraSeguro, MontoComisionApertura, TipoComision, IncluyePlacas, MontoPlacas. Texto: " +
                  textoPlano,
              },
            ],
          }),
        },
      );

      const dataOpenAI = await responseOpenAI.json();
      const textoOrdenado = dataOpenAI?.choices?.[0]?.message?.content || "";

      if (!textoOrdenado) {
        Swal.fire({
          icon: "warning",
          title: "PDF cargado",
          text: "OpenAI no devolvió información. Llena los datos manualmente.",
        });
        return;
      }

      const datos = parseOpenAIJson(textoOrdenado);

      const nombreCliente = getValue(datos, [
        "Cliente",
        "cliente",
        "Nombre",
        "nombre",
      ]);
      const telefono = getValue(datos, ["Telefono", "Teléfono", "telefono"]);
      const correo = getValue(datos, ["Correo", "correo", "Email", "email"]);
      const marca = getValue(datos, ["Marca", "marca"]);
      const modelo = getValue(datos, ["Modelo", "modelo"]);
      const anio = getValue(datos, ["Año", "Ano", "anio", "año"]);
      const version = getValue(datos, ["Version", "Versión", "version"]);
      const condicion = getValue(datos, [
        "TipoDeVehiculo",
        "TipoVehiculo",
        "tipoDeVehiculo",
      ]);

      const precioVehiculo = getValue(datos, [
        "PrecioVehiculo",
        "PrecioTotal",
        "Precio total",
        "precioVehiculo",
        "precioTotal",
      ]);
      const plazo = getValue(datos, ["PlazoMeses", "Plazo", "plazo"]);
      const enganche = getValue(datos, ["Enganche", "enganche"]);
      const mensualidad = getValue(datos, ["Mensualidad", "mensualidad"]);
      const tasa = getValue(datos, [
        "TasaDeInteres",
        "TazaDeInteres",
        "Tasa de Interes",
        "Tasa de Interés",
        "tasaDeInteres",
      ]);
      const financiera = getValue(datos, ["Financiera", "Banco", "banco"]);
      const plan = getValue(datos, [
        "PlanDeFinanciamiento",
        "Plan de Financiamiento",
      ]);
      const metodoContacto = getValue(datos, [
        "MetodoContacto",
        "MétodoContacto",
        "metodoContacto",
        "FuenteOrigen",
        "Fuente Origen",
        "fuenteOrigen",
        "ComoTeEnteraste",
        "CómoTeEnteraste",
      ]);
      const origenCliente = getValue(datos, [
        "OrigenCliente",
        "Origen Cliente",
        "origenCliente",
        "Fuente",
        "fuente",
      ]);
      const aseguradora = getValue(datos, ["Aseguradora", "aseguradora"]);
      const montoSeguro = getValue(datos, ["MontoSeguro", "montoSeguro"]);
      const tipoCompraSeguro = getValue(datos, [
        "TipoCompraSeguro",
        "tipoCompraSeguro",
      ]);
      const montoComisionApertura = getValue(datos, [
        "MontoComisionApertura",
        "ComisionApertura",
        "montoComisionApertura",
      ]);
      const tipoComision = getValue(datos, ["TipoComision", "tipoComision"]);
      const incluyePlacas = getValue(datos, [
        "IncluyePlacas",
        "incluyePlacas",
        "AgregoPlacas",
        "AgregóPlacas",
      ]);
      const montoPlacas = getValue(datos, [
        "MontoPlacas",
        "montoPlacas",
        "Placas",
      ]);

      setCliente((prev) => ({
        ...prev,
        nombre: nombreCliente || prev.nombre,
        telefono: telefono || prev.telefono,
        correo: correo || prev.correo,
      }));

      setSelectedVehicle((prev) => ({
        ...prev,
        brand: marca || prev.brand,
        model: modelo || prev.model,
        ano: anio || prev.ano,
        version: version || prev.version,
      }));

      if (condicion) {
        setCondicionVehiculo(
          normalizarTexto(condicion).includes("semi") ? "seminuevos" : "nuevos",
        );
      }

      setFinanciamiento((prev) => ({
        ...prev,
        precioVehiculo: precioVehiculo
          ? cleanNumberInput(precioVehiculo)
          : prev.precioVehiculo,
        enganche: enganche ? cleanNumberInput(enganche) : prev.enganche,
        tasa: tasa ? cleanNumberInput(tasa) : prev.tasa,
        mensualidad: mensualidad
          ? cleanNumberInput(mensualidad)
          : prev.mensualidad,
        plazo: plazo ? cleanNumberInput(plazo) : prev.plazo,
        financiera: financiera || prev.financiera,
        planFinanciamiento: plan || prev.planFinanciamiento,
        metodoContacto: METODO_CONTACTO_OPTIONS.includes(metodoContacto)
          ? metodoContacto
          : prev.metodoContacto,
        origenCliente: ORIGEN_CLIENTE_OPTIONS.includes(origenCliente)
          ? origenCliente
          : prev.origenCliente,
        aseguradora: aseguradora || prev.aseguradora,
        montoSeguro: montoSeguro
          ? cleanNumberInput(montoSeguro)
          : prev.montoSeguro,
        tipoCompraSeguro: tipoCompraSeguro || prev.tipoCompraSeguro,
        montoComisionApertura: montoComisionApertura
          ? cleanNumberInput(montoComisionApertura)
          : prev.montoComisionApertura,
        tipoComision: tipoComision || prev.tipoComision,
        incluyePlacas: incluyePlacas
          ? normalizarTexto(incluyePlacas).includes("si")
            ? "Si"
            : "No"
          : prev.incluyePlacas,
        montoPlacas: montoPlacas
          ? cleanNumberInput(montoPlacas)
          : prev.montoPlacas,
      }));

      Swal.fire({
        icon: "success",
        title: "PDF leído",
        text: "Se llenó la información detectada. Revisa los datos antes de guardar.",
      });
    } catch (error) {
      console.error("Error al procesar OCR/OpenAI:", error);

      Swal.fire({
        icon: "warning",
        title: "PDF cargado",
        text: "El PDF se cargó, pero no se pudo procesar automáticamente. Llena los datos manualmente.",
      });
    } finally {
      setLoading(false);
    }
  };

  const validateStep = (currentStep = step) => {
    const missing = [];

    if (currentStep === 1) {
      if (!selectedFile) missing.push("PDF cotización");
      if (!cliente.nombre) missing.push("Nombre cliente");
      if (!cliente.telefono) missing.push("Teléfono");
      if (!cliente.correo) missing.push("Correo");
    }

    if (currentStep === 2) {
      if (!selectedVehicle.brand) missing.push("Marca");
      if (!selectedVehicle.model) missing.push("Modelo");
      if (!selectedVehicle.ano) missing.push("Año");
      if (!selectedVehicle.version) missing.push("Versión");
      if (!condicionVehiculo) missing.push("Condición");
      if (!selectedAgencia) missing.push("Agencia");
      if (!financiamiento.userId) missing.push("Asesor");
    }

    if (currentStep === 3) {
      if (!financiamiento.precioVehiculo) missing.push("Precio vehículo");
      if (!financiamiento.enganche) missing.push("Enganche");
      if (!financiamiento.tasa) missing.push("Tasa");
      if (!financiamiento.mensualidad) missing.push("Mensualidad");
      if (!financiamiento.plazo) missing.push("Plazo");
      if (!financiamiento.financiera) missing.push("Financiera");
      if (!financiamiento.metodoContacto)
        missing.push("¿Cómo te enteraste de nosotros?");
      if (!financiamiento.origenCliente) missing.push("Origen del cliente");
      if (
        financiamiento.incluyePlacas === "Si" &&
        !financiamiento.montoPlacas
      ) {
        missing.push("Monto placas");
      }
    }

    if (missing.length) {
      Swal.fire({
        icon: "warning",
        title: "Campos incompletos",
        html: `Falta llenar:<br><strong>${missing.join(", ")}</strong>`,
      });
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, 4));
  };

  const handlePreviousStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const resetManualSaleState = () => {
    setStep(1);
    setIsSubmitted(false);
    setSelectedFile(null);
    setSelectedAccessoryIds([]);
    setTableData({ food: [] });
    setInventoryError("");
    setLoadingInventory(false);
    setSelectedAgencia("");
    setAsesores([]);
    setCargandoAsesores(false);
    setCliente(INITIAL_CLIENTE);
    setSelectedVehicle(INITIAL_SELECTED_VEHICLE);
    setFinanciamiento(INITIAL_FINANCIAMIENTO);
    setCondicionVehiculo("nuevos");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRegisterVehicle = async () => {
    if (isSubmitting || isSubmitted) return;

    for (let currentStep = 1; currentStep <= 3; currentStep += 1) {
      if (!validateStep(currentStep)) {
        setStep(currentStep);
        return;
      }
    }

    if (!selectedFile) {
      setStep(1);
      Swal.fire({
        icon: "warning",
        title: "PDF obligatorio",
        text: "Debes cargar el PDF de la cotización para guardar la venta manual.",
      });
      return;
    }

    const requestData = {
      access_token: ACCESS_TOKEN,

      nombre: cliente.nombre,
      telefono: cliente.telefono,
      correo: cliente.correo,

      marca: selectedVehicle.brand,
      modelo: selectedVehicle.model,
      anio: selectedVehicle.ano,
      version: selectedVehicle.version,
      condicion: tipoAutoNormalizado,
      tipo_auto: tipoAutoNormalizado,
      agencia: selectedAgencyName,
      ag_id: selectedAgencia,
      asesor: asesorNombre,
      usuario_id: financiamiento.userId,

      financiera: financiamiento.financiera,
      fuente: financiamiento.origenCliente,
      fuente_origen: financiamiento.metodoContacto,

      precio_vehiculo: precioVehiculoBase,
      precio_total: precioTotalFinal,
      financiamiento_meses: financiamiento.plazo,
      enganche: financiamiento.enganche,
      tasa: financiamiento.tasa,
      mensualidad_estimada: financiamiento.mensualidad,

      aseguradora: financiamiento.aseguradora,
      monto_seguro: financiamiento.montoSeguro,
      tipo_compra_seguro: financiamiento.tipoCompraSeguro,
      monto_comision_apertura: financiamiento.montoComisionApertura,
      tipo_comision: financiamiento.tipoComision,
      incluye_placas: financiamiento.incluyePlacas,
      monto_placas:
        financiamiento.incluyePlacas === "Si" ? financiamiento.montoPlacas : "",
      respuesta_prueba_manejo_cliente: financiamiento.respuestaPruebaManejo,

      plan_financiamiento: "Plan cliente",
      plan_accesorios: selectedAccessories,
      planes_seleccionados: selectedAccessories,
      planes_financiamiento: [planCliente],
      selected_car: selectedCarManual,
      order_cart: orderCartManual,

      venta_babilonia: "No",
    };

    const formData = new FormData();
    formData.append("pdf", selectedFile);

    Object.entries(requestData).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        formData.append(key, "");
        return;
      }

      formData.append(
        key,
        typeof value === "object" ? JSON.stringify(value) : String(value),
      );
    });

    try {
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE_URL}solicitudes/register`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (result.statusCode === 200) {
        await Swal.fire({
          icon: "success",
          title: "Venta manual guardada",
          text: result?.data?.folio_venta
            ? `Folio generado: ${result.data.folio_venta}`
            : "La venta manual se guardó correctamente.",
        });

        resetManualSaleState();
        return;
      }

      Swal.fire({
        icon: "warning",
        title: "No se pudo guardar",
        text: result?.error?.description || "Revisa los datos enviados.",
      });
    } catch (error) {
      console.error("Error al conectar con la API:", error);

      Swal.fire({
        icon: "error",
        title: "Error de conexión",
        text: "Hubo un error al procesar la solicitud.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepHeader = () => (
    <div className="nav-wizards-container mb-3">
      <nav className="nav nav-wizards-3 mb-2 manual-sale-wizard">
        {[
          { number: 1, label: "PDF y cliente" },
          { number: 2, label: "Vehículo" },
          { number: 3, label: "Financiamiento" },
          { number: 4, label: "Plan cliente" },
        ].map((item) => (
          <div className="nav-item col" key={item.number}>
            <a
              className={`nav-link ${step >= item.number ? "completed" : ""} ${
                step === item.number ? "active" : ""
              }`}
              href="#/"
              onClick={(e) => {
                e.preventDefault();
                if (item.number < step) setStep(item.number);
              }}
            >
              <div className="nav-dot"></div>
              <div className="nav-title">{item.number}</div>
              <div className="nav-text">{item.label}</div>
            </a>
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <div>
      <style>{`
        .manual-sale-page .form-control,
        .manual-sale-page .form-select {
          border-radius: 10px;
        }

        .manual-sale-hero {
          border: 1px solid rgba(13, 110, 253, .18);
          background: linear-gradient(135deg, rgba(13, 110, 253, .10), rgba(25, 135, 84, .08));
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 16px;
        }

        .manual-sale-summary-card {
          border: 1px solid rgba(0,0,0,.08);
          border-radius: 14px;
          background: #fff;
          padding: 14px;
          height: 100%;
          box-shadow: 0 3px 10px rgba(0,0,0,.04);
        }

        .manual-accessory-card {
          border: 1px solid rgba(0,0,0,.08);
          border-radius: 14px;
          padding: 14px;
          height: 100%;
          cursor: pointer;
          transition: all .15s ease;
          background: #fff;
        }

        .manual-accessory-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 18px rgba(0,0,0,.08);
        }

        .manual-accessory-card.is-selected {
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13,110,253,.14);
        }

        .manual-money-chip {
          border-radius: 999px;
          background: rgba(25, 135, 84, .10);
          color: #146c43;
          padding: 5px 10px;
          font-weight: 700;
          display: inline-block;
        }

        .manual-pill {
          border-radius: 999px;
          padding: 5px 10px;
          font-weight: 700;
          font-size: 12px;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .manual-pill-blue {
          background: rgba(13, 110, 253, .10);
          color: #084298;
        }

        .manual-pill-green {
          background: rgba(25, 135, 84, .10);
          color: #146c43;
        }
      `}</style>

      {loading ? (
        <div className="loader-data">
          <Circles height="80" width="80" color="#4fa94d" />
        </div>
      ) : (
        <div className="manual-sale-page">
          <div className="mailbox">
            <div className="mailbox-toolbar mb-3">
              {tabs.map((tab) =>
                modules.includes(tab.module) ? (
                  <div className="mailbox-toolbar-item" key={tab.name}>
                    <Link
                      to={tab.to}
                      className={`mailbox-toolbar-link ${
                        location.pathname === tab.to ? "active" : ""
                      }`}
                    >
                      {tab.name}
                    </Link>
                  </div>
                ) : null,
              )}
            </div>

            <div className="container-fluid">
              <Card>
                <CardHeader className="with-btn">
                  CREAR SOLICITUD
                  <CardHeaderBtn />
                </CardHeader>

                <CardBody>
                  {renderStepHeader()}

                  <div className="row g-3 mb-3">
                    <div className="col-md-3">
                      <div className="manual-sale-summary-card">
                        <div className="text-muted small">Vehículo</div>
                        <strong>{formatCurrency(precioVehiculoBase)}</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="manual-sale-summary-card">
                        <div className="text-muted small">Accesorios</div>
                        <strong>{formatCurrency(totalAccesorios)}</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="manual-sale-summary-card">
                        <div className="text-muted small">Total</div>
                        <strong>{formatCurrency(precioTotalFinal)}</strong>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="manual-sale-summary-card">
                        <div className="text-muted small">Mensualidad</div>
                        <strong>
                          {formatCurrency(financiamiento.mensualidad)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <Card>
                    <CardBody>
                      {step === 1 && (
                        <div>
                          <h5 className="black-text mb-3">PDF y cliente</h5>

                          <div className="alert alert-warning">
                            El PDF de cotización es obligatorio. Si el OCR
                            falla, el PDF queda cargado y puedes llenar los
                            datos manualmente.
                          </div>

                          <div className="row">
                            <div className="col-lg-12 mb-3">
                              <label htmlFor="pdfFile" className="black-text">
                                PDF Cotización{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="application/pdf"
                                onChange={handleFileChange}
                                className="form-control"
                                id="pdfFile"
                                disabled={isSubmitted}
                              />
                              {selectedFile && (
                                <div className="small text-success mt-1">
                                  Archivo cargado: {selectedFile.name}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label className="black-text">
                                Nombre cliente *
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={cliente.nombre}
                                onChange={(e) =>
                                  handleClienteChange("nombre", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Teléfono *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={cliente.telefono}
                                onChange={(e) =>
                                  handleClienteChange(
                                    "telefono",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label className="black-text">
                                Correo electrónico *
                              </label>
                              <input
                                type="email"
                                className="form-control"
                                value={cliente.correo}
                                onChange={(e) =>
                                  handleClienteChange("correo", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 2 && (
                        <div>
                          <h5 className="black-text mb-3">
                            Vehículo, agencia y asesor
                          </h5>

                          <div className="row">
                            <div className="col-lg-3 mb-3">
                              <label className="black-text">Condición *</label>
                              <select
                                className="form-control"
                                value={condicionVehiculo}
                                onChange={(e) => {
                                  setCondicionVehiculo(e.target.value);
                                  setSelectedAccessoryIds([]);
                                }}
                                disabled={isSubmitted}
                              >
                                <option value="nuevos">Nuevo</option>
                                <option value="seminuevos">Seminuevo</option>
                              </select>
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">Marca *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedVehicle.brand}
                                onChange={(e) =>
                                  handleVehicleChange("brand", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">Modelo *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedVehicle.model}
                                onChange={(e) =>
                                  handleVehicleChange("model", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">Año *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedVehicle.ano}
                                onChange={(e) =>
                                  handleVehicleChange("ano", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-12 mb-3">
                              <label className="black-text">Versión *</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedVehicle.version}
                                onChange={(e) =>
                                  handleVehicleChange("version", e.target.value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Agencia *</label>
                              <select
                                className="form-control custom-select"
                                onChange={handleAgenciaChange}
                                value={selectedAgencia}
                                disabled={isSubmitted}
                              >
                                <option value="">Seleccione agencia</option>
                                {agencias.map((agencia) => (
                                  <option
                                    key={agencia.ag_id}
                                    value={agencia.ag_id}
                                  >
                                    {agencia.nombre}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Asesor *</label>
                              <select
                                className="form-control custom-select"
                                value={financiamiento.userId || ""}
                                onChange={handleAsesorChange}
                                disabled={
                                  !selectedAgencia ||
                                  cargandoAsesores ||
                                  isSubmitted
                                }
                              >
                                <option value="">
                                  {!selectedAgencia
                                    ? "Primero selecciona una agencia"
                                    : cargandoAsesores
                                      ? "Cargando asesores..."
                                      : "Seleccione asesor"}
                                </option>
                                {asesoresOrdenados.map((asesor) => {
                                  const asesorId = getAsesorId(asesor);
                                  const asesorNombre =
                                    getAsesorNombreDisplay(asesor);

                                  return (
                                    <option key={asesorId} value={asesorId}>
                                      {asesorNombre}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 3 && (
                        <div>
                          <h5 className="black-text mb-3">
                            Financiamiento, seguro y cargos
                          </h5>

                          <div className="row">
                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                Precio vehículo *
                              </label>
                              <MoneyInput
                                value={financiamiento.precioVehiculo}
                                onChange={(value) =>
                                  handleFinanciamientoChange(
                                    "precioVehiculo",
                                    value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-4 mb-3">
                              <label className="black-text">Enganche *</label>
                              <MoneyInput
                                value={financiamiento.enganche}
                                onChange={(value) =>
                                  handleFinanciamientoChange("enganche", value)
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                Mensualidad *
                              </label>
                              <MoneyInput
                                value={financiamiento.mensualidad}
                                onChange={(value) =>
                                  handleFinanciamientoChange(
                                    "mensualidad",
                                    value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-4 mb-3">
                              <label className="black-text">Tasa (%) *</label>
                              <input
                                type="text"
                                inputMode="decimal"
                                className="form-control"
                                value={formatThousands(financiamiento.tasa)}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "tasa",
                                    cleanNumberInput(e.target.value),
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-4 mb-3">
                              <label className="black-text">Plazo *</label>
                              <select
                                className="form-control"
                                value={financiamiento.plazo}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "plazo",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona plazo</option>
                                <option value="12">12 meses</option>
                                <option value="24">24 meses</option>
                                <option value="36">36 meses</option>
                                <option value="48">48 meses</option>
                                <option value="60">60 meses</option>
                                <option value="72">72 meses</option>
                              </select>
                            </div>

                            <div className="col-lg-4 mb-3">
                              <label className="black-text">Financiera *</label>
                              <select
                                className="form-control"
                                value={financiamiento.financiera}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "financiera",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona financiera</option>
                                <option value="NR Finance">NR Finance</option>
                                <option value="Banorte">Banorte</option>
                                <option value="Scotiabank">Scotiabank</option>
                                <option value="Cibanco">Cibanco</option>
                                <option value="Santander">Santander</option>
                                <option value="BBVA">BBVA</option>
                                <option value="HSBC">HSBC</option>
                                <option value="Hey Banco">Hey Banco</option>
                                <option value="Sicrea">Sicrea</option>
                                <option value="Cetelem">Cetelem</option>
                                <option value="VWFS">VWFS</option>
                                <option value="Kuna">Kuna</option>
                                <option value="Mstar">Mstar</option>
                                <option value="Credito Go">Credito Go</option>
                                <option value="Finanmadrid">Finanmadrid</option>
                                <option value="Nexu">Nexu</option>
                              </select>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label
                                htmlFor="metodoContacto"
                                className="new-font form-label black-text"
                              >
                                ¿Cómo te enteraste de nosotros? *
                              </label>
                              <select
                                id="metodoContacto"
                                className="new-font form-select"
                                value={financiamiento.metodoContacto}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "metodoContacto",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona una opción</option>
                                {METODO_CONTACTO_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-lg-6 mb-3">
                              <label
                                htmlFor="origenCliente"
                                className="new-font form-label black-text"
                              >
                                Origen del Cliente *
                              </label>
                              <select
                                id="origenCliente"
                                className="new-font form-select"
                                value={financiamiento.origenCliente}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "origenCliente",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona el origen</option>
                                {ORIGEN_CLIENTE_OPTIONS.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Aseguradora</label>
                              <input
                                type="text"
                                className="form-control"
                                value={financiamiento.aseguradora}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "aseguradora",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-6 mb-3">
                              <label className="black-text">Monto seguro</label>
                              <MoneyInput
                                value={financiamiento.montoSeguro}
                                onChange={(value) =>
                                  handleFinanciamientoChange(
                                    "montoSeguro",
                                    value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-lg-3 mb-3">
                              <label className="black-text">
                                Tipo compra seguro
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.tipoCompraSeguro}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "tipoCompraSeguro",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona</option>
                                <option value="Contado">Contado</option>
                                <option value="Financiado">Financiado</option>
                              </select>
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">
                                Comisión apertura
                              </label>
                              <MoneyInput
                                value={financiamiento.montoComisionApertura}
                                onChange={(value) =>
                                  handleFinanciamientoChange(
                                    "montoComisionApertura",
                                    value,
                                  )
                                }
                                disabled={isSubmitted}
                              />
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">
                                Tipo comisión
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.tipoComision}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "tipoComision",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Selecciona</option>
                                <option value="Contado">Contado</option>
                                <option value="Financiado">Financiado</option>
                              </select>
                            </div>

                            <div className="col-lg-3 mb-3">
                              <label className="black-text">
                                ¿Se agregaron placas?
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.incluyePlacas}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "incluyePlacas",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="No">No</option>
                                <option value="Si">Sí</option>
                              </select>
                            </div>
                          </div>

                          {financiamiento.incluyePlacas === "Si" && (
                            <div className="row">
                              <div className="col-lg-4 mb-3">
                                <label className="black-text">
                                  Monto placas *
                                </label>
                                <MoneyInput
                                  value={financiamiento.montoPlacas}
                                  onChange={(value) =>
                                    handleFinanciamientoChange(
                                      "montoPlacas",
                                      value,
                                    )
                                  }
                                  disabled={isSubmitted}
                                />
                              </div>
                            </div>
                          )}

                          <div className="row">
                            <div className="col-lg-4 mb-3">
                              <label className="black-text">
                                Respuesta prueba manejo
                              </label>
                              <select
                                className="form-control"
                                value={financiamiento.respuestaPruebaManejo}
                                onChange={(e) =>
                                  handleFinanciamientoChange(
                                    "respuestaPruebaManejo",
                                    e.target.value,
                                  )
                                }
                                disabled={isSubmitted}
                              >
                                <option value="">Sin respuesta</option>
                                <option value="Si">Sí</option>
                                <option value="No">No</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {step === 4 && (
                        <div>
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <div>
                              <h5 className="black-text mb-1">Plan cliente</h5>
                              <div className="text-muted">
                                Accesorios y garantías cargados desde el
                                inventario vigente.
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={cargarInventarioAccesorios}
                              disabled={loadingInventory || isSubmitted}
                            >
                              {loadingInventory
                                ? "Cargando..."
                                : "Recargar inventario"}
                            </button>
                          </div>

                          {!selectedAgencia || !selectedVehicle.brand ? (
                            <div className="alert alert-info">
                              Selecciona agencia y marca para cargar accesorios
                              y garantías.
                            </div>
                          ) : null}

                          {inventoryError && (
                            <div className="alert alert-warning">
                              {inventoryError}
                            </div>
                          )}

                          <div className="alert alert-primary">
                            <strong>Plan:</strong> Plan cliente
                            <br />
                            <strong>Seleccionados:</strong>{" "}
                            {selectedAccessories.length}
                            <br />
                            <strong>Total accesorios:</strong>{" "}
                            {formatCurrency(totalAccesorios)}
                            <br />
                            <strong>Precio vehículo:</strong>{" "}
                            {formatCurrency(precioVehiculoBase)}
                            <br />
                            <strong>Precio total final:</strong>{" "}
                            {formatCurrency(precioTotalFinal)}
                          </div>

                          {loadingInventory ? (
                            <div className="text-center py-4">
                              <Circles height="60" width="60" color="#4fa94d" />
                            </div>
                          ) : (
                            <div className="row">
                              {filteredAccessoriesAndWarranties.length ? (
                                filteredAccessoriesAndWarranties.map((item) => {
                                  const itemId = String(item.__manual_id);
                                  const isSelected =
                                    selectedAccessoryIds.includes(itemId);
                                  const normalized =
                                    normalizeAccessoryForSave(item);

                                  return (
                                    <div className="col-md-4 mb-3" key={itemId}>
                                      <div
                                        className={`manual-accessory-card ${
                                          isSelected ? "is-selected" : ""
                                        }`}
                                        onClick={() => {
                                          if (!isSubmitted)
                                            toggleAccessory(item);
                                        }}
                                      >
                                        <div className="d-flex justify-content-between gap-2 mb-2">
                                          <span
                                            className={`badge ${
                                              normalizarTexto(item.type) ===
                                              "garantias"
                                                ? "bg-success"
                                                : "bg-primary"
                                            }`}
                                          >
                                            {normalizarTexto(item.type) ===
                                            "garantias"
                                              ? "Garantía"
                                              : "Accesorio"}
                                          </span>

                                          {isSelected ? (
                                            <span className="badge bg-dark">
                                              Seleccionado
                                            </span>
                                          ) : (
                                            <span className="badge bg-secondary">
                                              Disponible
                                            </span>
                                          )}
                                        </div>

                                        <h6 className="mb-2">
                                          {normalized.title}
                                        </h6>
                                        <p className="text-muted small mb-3">
                                          {normalized.description ||
                                            "Sin descripción"}
                                        </p>
                                        <span className="manual-money-chip">
                                          {formatCurrency(normalized.price)}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="col-12">
                                  <div className="alert alert-light border">
                                    No hay accesorios o garantías disponibles
                                    para esta agencia, marca, modelo y
                                    condición.
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="text-center mt-4">
                        {step > 1 && (
                          <button
                            type="button"
                            className="btn btn-secondary mx-2"
                            onClick={handlePreviousStep}
                            disabled={isSubmitting}
                          >
                            Anterior
                          </button>
                        )}

                        {step < 4 && (
                          <button
                            type="button"
                            className="btn btn-primary mx-2"
                            onClick={handleNextStep}
                            disabled={isSubmitting}
                          >
                            Siguiente
                          </button>
                        )}

                        {step === 4 && (
                          <button
                            type="button"
                            className="btn btn-success mx-2"
                            onClick={handleRegisterVehicle}
                            disabled={isSubmitting || isSubmitted}
                          >
                            {isSubmitting
                              ? "Guardando..."
                              : isSubmitted
                                ? "Guardado"
                                : "Guardar venta manual"}
                          </button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateRequest;
